/**
 * One typeahead for every choice over a real list: people, units, programs,
 * instances, zones. A select is only right for a short closed enumeration
 * (yes/no, a weekday); anything you would want to type at belongs here.
 *
 * ARIA 1.2 combobox with a listbox popup: the input owns the listbox, the
 * active option is named by aria-activedescendant, and the arrow keys, Enter,
 * Escape, Home and End all work without a pointer.
 */
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

export interface PickerOption {
  value: string;
  label: string;
  /** Shown under the label and searched with it: an address, a key, a role. */
  hint?: string;
}

export interface PickerProps {
  options: PickerOption[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  /** Show the label above the field rather than only to a screen reader. */
  showLabel?: undefined | boolean;
  placeholder?: undefined | string;
  /** Allow clearing back to "". */
  clearable?: undefined | boolean;
  disabled?: undefined | boolean;
  id?: undefined | string;
  /**
   * Accept what was typed as well as what is listed, shown as the top option under this hint.
   * For a field whose list is what Trellis has seen rather than everything that is allowed: a
   * username on a linked system is a real value even the first time anybody records it.
   */
  allowCustom?: undefined | string;
}

/** Prefix beats word-start beats anywhere, so typing "fa" puts Faraaz above Mustafa. */
export function rank(option: PickerOption, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const fields = [option.label, option.hint ?? ""].map((f) => f.toLowerCase());
  let best = 0;
  for (const f of fields) {
    if (f.startsWith(q)) return 3;
    if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(f)) best = Math.max(best, 2);
    else if (f.includes(q)) best = Math.max(best, 1);
  }
  return best;
}

export function filterOptions(options: PickerOption[], query: string): PickerOption[] {
  if (!query.trim()) return options;
  return options
    .map((o) => ({ o, r: rank(o, query) }))
    .filter((x) => x.r > 0)
    .sort((a, b) => b.r - a.r || a.o.label.localeCompare(b.o.label))
    .map((x) => x.o);
}

/**
 * What was typed, ahead of the matches, when it is not already one of them. Typing the whole of a
 * listed value offers it once, not twice, and a blank query offers nothing.
 */
export function withCustom(shown: PickerOption[], query: string, hint: string): PickerOption[] {
  const q = query.trim();
  if (!q || shown.some((o) => o.value === q)) return shown;
  return [{ value: q, label: q, hint }, ...shown];
}

let seq = 0;

export function Picker({ options, value, onChange, label, showLabel, placeholder, clearable, disabled, id, allowCustom }: PickerProps) {
  const base = useMemo(() => id ?? `pick-${++seq}`, [id]);
  const listId = `${base}-list`;
  const chosen = options.find((o) => o.value === value);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const shown = useMemo(() => {
    const matches = filterOptions(options, open ? query : "");
    return allowCustom && open ? withCustom(matches, query, allowCustom) : matches;
  }, [options, query, open, allowCustom]);

  // A chosen option replaces the query with its own label. A typed value is already the query, and
  // clearing it there would empty the field on every keystroke.
  useEffect(() => { if (allowCustom && value === query.trim()) return; setQuery(""); }, [value]);
  useEffect(() => { if (active >= shown.length) setActive(0); }, [shown.length, active]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const commit = (o: PickerOption) => { onChange(o.value); setQuery(""); setOpen(false); input.current?.focus(); };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || (e.key === "ArrowUp" && !open)) {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      setActive((i) => Math.min(i + 1, shown.length - 1));
      return;
    }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
    if (e.key === "Home" && open) { e.preventDefault(); setActive(0); return; }
    if (e.key === "End" && open) { e.preventDefault(); setActive(shown.length - 1); return; }
    if (e.key === "Enter" && open) {
      const pick = shown[active];
      if (pick) { e.preventDefault(); commit(pick); }
      return;
    }
    if (e.key === "Escape") { if (open) { e.preventDefault(); setOpen(false); setQuery(""); } return; }
    if (e.key === "Tab") setOpen(false);
  };

  // What the field reads: the typed query while searching, otherwise the chosen label. A custom
  // value is not in the list, so it reads back as itself.
  const text = open ? query : (chosen?.label ?? (allowCustom ? value : ""));

  return (
    <div class="picker" ref={box}>
      <label class={showLabel ? "s" : "sr-only"} for={base}>{label}</label>
      <div class="picker-field">
        <input
          ref={input}
          id={base}
          type="text"
          role="combobox"
          autocomplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          {...(open && shown[active] ? { "aria-activedescendant": `${base}-o${active}` } : {})}
          disabled={disabled}
          placeholder={placeholder ?? label}
          value={text}
          onFocus={() => setOpen(true)}
          // A field that takes what you typed reports it as it is typed. Waiting for Enter or a
          // click on the option left the parent holding "", so the button beside it stayed
          // disabled, and a disabled button swallows the press rather than reporting it.
          onInput={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setQuery(v); setOpen(true); setActive(0);
            if (allowCustom) onChange(v.trim());
          }}
          onKeyDown={onKey}
        />
        {clearable && (chosen || (allowCustom && value)) && !open && (
          <button type="button" class="picker-clear" aria-label={`Clear ${label}`} onClick={() => { onChange(""); setQuery(""); input.current?.focus(); }}>×</button>
        )}
      </div>
      <ul class={`picker-list${open ? "" : " gone"}`} id={listId} role="listbox" aria-label={label}>
        {shown.map((o, i) => (
          <li
            key={o.value}
            id={`${base}-o${i}`}
            role="option"
            aria-selected={o.value === value}
            class={`picker-option${i === active ? " active" : ""}`}
            onMouseDown={(e) => { e.preventDefault(); commit(o); }}
            onMouseEnter={() => setActive(i)}
          >
            <span>{o.label}</span>
            {o.hint && <span class="s">{o.hint}</span>}
          </li>
        ))}
        {open && shown.length === 0 && <li class="picker-option muted" role="option" aria-selected={false} aria-disabled="true">No match</li>}
      </ul>
    </div>
  );
}
